import React from 'react';
import type { FC } from 'react';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

type EditorDisplayWrapperProps = {
	isHidden: boolean;
	isEditorActionBarRendered?: boolean;
	/** when enabled remove margin overrides on comment primary toolbar */
	isToolbarAifcEnabled?: boolean;
};

const baseStyles = css({
	flex: 1,
	minHeight: 0,
	position: 'relative',
	/* CEMS-3103 - fix margin top being overridden to 0 by wiki-content class */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	[`.fabric-editor-indentation-mark > h1, .fabric-editor-indentation-mark.fabric-editor-block-mark[data-level="1"]:first-of-type > h1:first-of-type`]:
		{
			marginTop: token('space.500'), // Originally h700().marginTop
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.fabric-editor-indentation-mark > h2:first-of-type': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- Grandfathered in as part of USS-50, these actually may break performance w/ Compiled, Ignored via go/DSP-18766
		marginTop: '28px', // Originally h600().marginTop
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.fabric-editor-indentation-mark > h3:first-of-type': {
		marginTop: token('space.300'), // Originally h500().marginTop
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.fabric-editor-indentation-mark > h4:first-of-type': {
		marginTop: token('space.200'), // Originally h400().marginTop
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.fabric-editor-indentation-mark > h5:first-of-type': {
		marginTop: token('space.250'), // Originally h300().marginTop
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.fabric-editor-indentation-mark > h6:first-of-type': {
		marginTop: token('space.200'), // Originally h100().marginTop
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.ak-ul li > ul': {
		marginTop: token('space.050'),
	},
});

/* hide the editor when the page is being previewed */
const hiddenStyles = css({
	display: 'none',
});

const visibleStyles = css({
	display: 'unset',
});

const overflowHiddenStyles = css({
	overflowY: 'hidden',
});

const toolbarMarginStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"[data-testid='ak-editor-main-toolbar'] > div[role='toolbar']": {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		marginLeft: token('space.300'),
	},
});

const toolbarNoMarginStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"[data-testid='ak-editor-main-toolbar'] > div[role='toolbar']": {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		marginLeft: token('space.0'),
	},
});

/**
 * Component wrapping the editor to apply styling overrides
 */
export const EditorDisplayWrapper: FC<
	EditorDisplayWrapperProps & { children: React.ReactNode }
> = ({ isHidden, isEditorActionBarRendered, isToolbarAifcEnabled, ...props }) => {
	const shouldApplyToolbarMargin = isEditorActionBarRendered && !isToolbarAifcEnabled;

	return (
		<div
			css={[
				baseStyles,
				isHidden ? hiddenStyles : visibleStyles,
				isEditorActionBarRendered && overflowHiddenStyles,
				shouldApplyToolbarMargin ? toolbarMarginStyles : toolbarNoMarginStyles,
			]}
			{...props}
		/>
	);
};
